import { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Modal,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Box,
  Badge,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  listPromptVersions,
  activatePromptVersion,
  deletePromptVersion,
  type PromptVersion,
} from '@mygpt/app/apis/services/prompt-versions';
import { VersionComparisonModal } from './VersionComparisonModal';

interface VersionHistoryDrawerProps {
  opened: boolean;
  onClose: () => void;
  aiId: string;
  promptType: 'main' | 'realtime' | 'phone';
  currentPrompt: string;
  onVersionActivated: () => void;
}

export const VersionHistoryDrawer = ({
  opened,
  onClose,
  aiId,
  promptType,
  currentPrompt,
  onVersionActivated,
}: VersionHistoryDrawerProps) => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [operating, setOperating] = useState(false);

  // 预览 Modal
  const [previewData, setPreviewData] = useState<{ title: string; content: string } | null>(null);

  // 版本比較 Modal
  const [comparisonOpened, setComparisonOpened] = useState(false);
  const [comparisonInitialVersionId, setComparisonInitialVersionId] = useState<string | null>(null);

  const fetchVersions = useCallback(async () => {
    if (!aiId || !opened) return;
    setLoading(true);
    try {
      const data = await listPromptVersions(aiId, promptType, 1, 50);
      setVersions(data.items);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [aiId, promptType, opened]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // 当前是否有未保存的变更
  const activeVersion = versions.find((v) => v.is_active);
  const hasUnsavedChanges = activeVersion ? currentPrompt !== activeVersion.prompt : false;

  const handleRestore = async (version: PromptVersion) => {
    if (operating || version.is_active) return;
    setOperating(true);
    try {
      await activatePromptVersion(aiId, version.id);
      showNotification({ message: t('settings.prompt.version_activated'), color: 'green' });
      onVersionActivated();
      await fetchVersions();
    } catch {
      showNotification({ message: t('settings.prompt.version_activate_error'), color: 'red' });
    } finally {
      setOperating(false);
    }
  };

  const handlePreview = (title: string, content: string) => {
    setPreviewData({ title, content });
  };

  const handleCompare = (version: PromptVersion) => {
    setComparisonInitialVersionId(version.id);
    setComparisonOpened(true);
  };

  const handleDeleteAll = async () => {
    if (operating) return;
    if (!window.confirm(t('settings.prompt.delete_all_confirm'))) return;
    setOperating(true);
    try {
      const nonActive = versions.filter((v) => !v.is_active);
      for (const v of nonActive) {
        await deletePromptVersion(aiId, v.id);
      }
      showNotification({ message: t('settings.prompt.version_deleted'), color: 'green' });
      await fetchVersions();
      // 通知父组件刷新「Version History (N)」计数，避免 Delete all 后计数过期（与 handleRestore 保持一致）
      onVersionActivated();
    } catch {
      showNotification({ message: t('settings.prompt.version_delete_error'), color: 'red' });
    } finally {
      setOperating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const promptTypeLabel =
    promptType === 'main'
      ? t('settings.prompt.main_prompt_tab')
      : promptType === 'phone'
      ? t('settings.prompt.phone_prompt_tab')
      : t('settings.prompt.realtime_prompt_tab');

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title={`${t('settings.prompt.version_history')} - ${promptTypeLabel}`}
        position="right"
        size="md"
        styles={{ body: { display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' } }}
      >
        <ScrollArea style={{ flex: 1 }}>
          <Stack gap="sm">
            {/* 現在 */}
            <Box
              p="sm"
              style={{
                borderLeft: '3px solid var(--mantine-color-blue-5)',
                backgroundColor: 'var(--mantine-color-blue-0)',
                borderRadius: 4,
              }}
            >
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  {hasUnsavedChanges ? t('settings.prompt.current_unsaved') : t('settings.prompt.current_saved')}
                </Text>
              </Group>
              {activeVersion && (
                <Text size="xs" c="dimmed" mt={2}>
                  {t('settings.prompt.last_updated')}: {formatDate(activeVersion.created_at)}
                </Text>
              )}
              <Group gap="xs" mt="xs">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => handlePreview(t('settings.prompt.current_saved'), currentPrompt || '(empty)')}
                >
                  {t('settings.prompt.preview')}
                </Button>
                {activeVersion && (
                  <Button variant="subtle" size="compact-xs" onClick={() => handleCompare(activeVersion)}>
                    {t('settings.prompt.compare')}
                  </Button>
                )}
              </Group>
            </Box>

            <Divider />

            {/* 加载中 */}
            {loading && (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            )}

            {/* 版本列表 */}
            {!loading &&
              versions.map((version) => (
                <Box
                  key={version.id}
                  p="sm"
                  style={{
                    borderLeft: version.is_active
                      ? '3px solid var(--mantine-color-green-5)'
                      : '3px solid var(--mantine-color-gray-3)',
                    borderRadius: 4,
                  }}
                >
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {formatDate(version.created_at)}
                    </Text>
                    {version.is_active && (
                      <Badge size="xs" color="green">
                        {t('settings.prompt.active')}
                      </Badge>
                    )}
                  </Group>
                  {version.change_summary && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {t('settings.prompt.change_description')}: &quot;{version.change_summary}&quot;
                    </Text>
                  )}
                  <Group gap="xs" mt="xs">
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={() =>
                        handlePreview(
                          `v${version.version} - ${formatDate(version.created_at)}`,
                          version.prompt || '(empty)'
                        )
                      }
                    >
                      {t('settings.prompt.preview')}
                    </Button>
                    {!version.is_active && (
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color="blue"
                        onClick={() => handleRestore(version)}
                        disabled={operating}
                      >
                        {t('settings.prompt.restore')}
                      </Button>
                    )}
                    <Button variant="subtle" size="compact-xs" onClick={() => handleCompare(version)}>
                      {t('settings.prompt.compare')}
                    </Button>
                  </Group>
                </Box>
              ))}

            {!loading && versions.length === 0 && (
              <Text c="dimmed" ta="center" py="md">
                {t('settings.prompt.no_versions')}
              </Text>
            )}
          </Stack>
        </ScrollArea>

        {/* 底部 */}
        <Stack gap="xs" mt="md">
          <Text size="xs" c="dimmed">
            {t('settings.prompt.max_versions_notice')}
          </Text>
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={handleDeleteAll}
              disabled={operating || versions.length <= 1}
            >
              {t('settings.prompt.delete_all')}
            </Button>
            <Button variant="default" size="xs" onClick={onClose}>
              {t('settings.prompt.close')}
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* 预览 Modal */}
      <Modal opened={!!previewData} onClose={() => setPreviewData(null)} title={previewData?.title} size="lg">
        <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: '70vh', overflow: 'auto', fontSize: 13 }}>
          {previewData?.content || ''}
        </Code>
      </Modal>

      {/* 版本比較 Modal */}
      <VersionComparisonModal
        opened={comparisonOpened}
        onClose={() => setComparisonOpened(false)}
        aiId={aiId}
        promptType={promptType}
        versions={versions}
        initialVersionAId={comparisonInitialVersionId}
        currentPrompt={currentPrompt}
        onVersionActivated={() => {
          onVersionActivated();
          fetchVersions();
        }}
      />
    </>
  );
};
